import { redirect } from 'react-router-dom';
import { getParamsFromStoreRedux } from 'store/utils';
import {
  getReturnUrlFromRequestLoader,
  getPostFlowUrl,
} from 'utils/common/route';
import { ensureUserInfoLoaded } from 'utils/loaderUtils';
import { PERMISSION } from 'static/Permission';
import { isEmpty, isObject } from 'lodash';
import * as ssoProductActions from 'pages/sso-product/controllers/actions';
import { queryBrandingNavigation } from 'hooks';

export const preRoutingLoader =
  (queryClient) =>
  async ({ request }) => {
    const { dispatch, getState } = getParamsFromStoreRedux();

    if (!dispatch || !getState) {
      throw redirect('/login');
    }

    const returnUrl = getReturnUrlFromRequestLoader(request);
    const state = getState();

    try {
      const { userInfo } = await ensureUserInfoLoaded({
        queryClient,
        returnUrl,
      });

      const { data: brandingData } =
        (await queryBrandingNavigation({
          queryClient,
          userId: userInfo?.id,
        })) ?? {};

      // Get SSO product data from Redux state
      const bpSsoProductData = state?.ssoProduct?.bpSsoProductData;
      const bpSsoProductSuccess = state?.ssoProduct?.bpSsoProductSuccess; // Add this selector

      const extractSsoProductParams = (ssoData, ssoSuccess) => {
        if (!ssoData) return { ssoProductParams: '', productId: null };

        // Get productId from either source (matching the hook logic)
        const productId = +ssoData?.productId || ssoSuccess?.productId;

        // Build SSO product parameters string (matching the hook logic)
        const ssoProductParams = `projectId=${ssoData?.projectId}&supplierId=${ssoData?.supplierId}&retailerId=${ssoData?.retailerId}&sso=true`;

        return {
          ssoProductParams,
          productId,
        };
      };

      const { ssoProductParams, productId } = extractSsoProductParams(
        bpSsoProductData,
        bpSsoProductSuccess
      );

      if (bpSsoProductData) {
        // Check if branding data is properly loaded
        const isBrandingReady =
          brandingData &&
          isObject(brandingData) &&
          Object.keys(brandingData).length > 2;

        if (!isBrandingReady) {
          // If branding data isn't ready, wait for it or redirect to get it
          console.warn('Branding data not ready for SSO product validation');
          // You might want to handle this case differently based on your needs
        }

        // Process SSO product routing
        if (
          isEmpty(bpSsoProductData?.productId) ||
          bpSsoProductData?.productId === 'null'
        ) {
          // Create new product scenario
          const isSuperAdmin = userInfo?.isSuperMember;
          const createProduct = userInfo?.permissions?.includes(
            PERMISSION.CREATE_PRODUCT
          );

          if (isSuperAdmin || createProduct) {
            throw redirect(`/product/createnew?${ssoProductParams}`);
          } else {
            throw redirect('/products');
          }
        } else {
          // View existing product scenario
          const isSuperAdmin = userInfo?.isSuperMember;
          const viewProduct = userInfo?.permissions?.includes(
            PERMISSION.VIEW_PRODUCTS
          );

          if (isSuperAdmin || viewProduct) {
            // Set SSO product validation flag
            dispatch(ssoProductActions.bpSsoProductValidateDetail(true));
            throw redirect(`/product/${productId}?${ssoProductParams}`);
          } else {
            throw redirect('/products');
          }
        }
      } else {
        // Normal route with post flow
        const redirectUrl = getPostFlowUrl({
          type: 'prerouting',
          returnUrl,
          dispatch,
        });

        throw redirect(redirectUrl);
      }
    } catch (error) {
      // If it's already a redirect, re-throw it
      if (error?.status === 302 || error instanceof Response) {
        throw error;
      }

      // For other errors, redirect to login or dashboard
      throw redirect(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  };
